import { Text } from '@avanta/shared/components/Text';
import { Button } from '@avanta/shared/components/ui/Button';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { CalendarCog, FileText, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ENTERPRISE_APPOINTMENT_STATUS } from 'constants/appointments';
import { useAppointmentAction } from 'hooks/useAppointmentAction';
import { type AppointmentWithDetails } from 'types/appointments';

interface ActionProps {
  appointment: AppointmentWithDetails;
}

const ManageSessionAction = ({ appointment }: ActionProps): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);
  const { runAppointmentAction } = useAppointmentAction();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    // The row already triggers this same action; avoid running it twice.
    event.stopPropagation();
    runAppointmentAction(appointment);
  };

  return (
    <Button variant="terciary" icon="leading" className="w-full" onClick={handleClick}>
      <CalendarCog className="size-5" />
      {t('SessionsPage.actions.manageSession')}
    </Button>
  );
};

const SessionDetailsAction = ({ appointment }: ActionProps): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);
  const { runAppointmentAction } = useAppointmentAction();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    runAppointmentAction(appointment);
  };

  return (
    <Button variant="terciary" icon="leading" className="w-full" onClick={handleClick}>
      <Info className="size-5" />
      {t('SessionsPage.actions.sessionDetails')}
    </Button>
  );
};

const ViewReportAction = ({ appointment }: ActionProps): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);
  const { runAppointmentAction, isReportPending } = useAppointmentAction();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    runAppointmentAction(appointment);
  };

  return (
    <Button
      variant="terciary"
      icon="leading"
      className="w-full"
      isLoading={isReportPending}
      onClick={handleClick}
    >
      <FileText className="size-5" />
      {t('SessionsPage.actions.viewReport')}
    </Button>
  );
};

export const TableActions = ({
  appointment,
}: {
  appointment: AppointmentWithDetails;
}): React.ReactElement | null => {
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);
  const { status } = appointment;

  if (status === ENTERPRISE_APPOINTMENT_STATUS.CANCELLED) {
    return <SessionDetailsAction appointment={appointment} />;
  }

  if (status === ENTERPRISE_APPOINTMENT_STATUS.DONE) {
    return (
      <div data-no-row-action>
        <Text variant="text-xs-regular" className="text-denim-800 text-center">
          {t('SessionsPage.actions.noReport')}
        </Text>
      </div>
    );
  }

  if (status === ENTERPRISE_APPOINTMENT_STATUS.COMPLETED) {
    return <ViewReportAction appointment={appointment} />;
  }

  return <ManageSessionAction appointment={appointment} />;
};
